<template>
  <div class="leftCardContainer">
    <div class="rightSideTitleActions">
      <span class="leftTitle">{{ $tc('Company', 2) }}</span>
      <div class="actionsContainer">
        <div class="searchContainer">
          <v-icon> search </v-icon>
          <InputField inputType="text" :value="search" :placeholder="$t('Search')" :inputStyle="{
            borderRadius: '0px',
            width: '130px',
            borderTop: 'none',
            borderRight: 'none',
            borderLeft: 'none',
            borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
          }" @input="handleSearch($event)" />
        </div>
        <CustomButton
          icon="delete"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :disabled="!selectedObjectID || !userHasPermission('company_destroy')"
          :buttonStyling="{ padding: '8px', marginRight: '5px', borderRadius: '4px' }"
          @buttonClick="openDeleteDialog"
        />
        <CustomButton
          icon="add"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="{ padding: '8px', marginRight: '5px', borderRadius: '4px' }"
          :disabled="!userHasPermission('company_store') || userIsCreatingOrEditingData"
          @buttonClick="handleAdd"
        />
      </div>
    </div>
    <div class="tabsContainer">
      <Tabs :activeTab="activeTab" :tabs="tabs" :center-tabs="false" @startTabChange="handleTabChange" />
    </div>
    <div class="tableDivContainer">
      <Table :checkbox="false" :loading="loading" :headers="headers" :items="companies" :sortable="true"
        @onScrollBottom="onScrollBottom" @onScrollTop="onScrollTop" @sortClick="onSortClick" :emitRowClickBusEvent="!userIsCreatingOrEditingData"
      />
    </div>
    <dialog-choice
      :isOpen="deleteDialogIsOpen"
      :title="$t('DeleteCompany')"
      :content="$t('DeleteCompanyConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="handleDelete"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="deleteDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="#29BDF0"
    />
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="#29BDF0"
      style="position: absolute;"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { companyListsMixin } from "@/mixins/management/companyListsMixin";
import { canEditCompanyMixin } from "@/mixins/management/canEditCompanyMixin";
import ManagementPage from "@/newComponents/_Organisms/ManagementPage.vue";
import Table from "@/newComponents/_Molecules/Table.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import InputField from "@/newComponents/_Atoms/InputField.vue";
import Tabs from "@/newComponents/_Atoms/Tabs.vue";
import DialogContent from "@/newComponents/_Molecules/DialogContent.vue";
import LoadingSpinner from "vue-loading-overlay";
import DialogChoice from '@/newComponents/_Molecules/DialogChoice.vue';
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "TableView",

  mixins: [managementDatatableMixin, companyListsMixin, canEditCompanyMixin, checkPermissionsMixin],

  components: {
    Table,
    ManagementPage,
    CustomButton,
    InputField,
    Tabs,
    DialogContent,
    LoadingSpinner,
    DialogChoice,
  },

  props: {
    selectedObjectID: {
      type: Number,
    },
  },

  data() {
    return {
      dialog: false,
      deleteDialogIsOpen: false,
      value: null,
      type: "normal",
      search: null,
      activeTab: 0,
      loading: false,
      scrollTop: 0,
      sortOrder: null,
      scrollHeight: 0,
      tabs: [
        {
          label: this.$i18n.t('ParentCompany'),
        },
        {
          label: this.$i18n.t('Level') + ' 1',
        },
        {
          label: this.$i18n.t('Level') + ' 2',
        },
      ],
      pageNumber: 1,
      pagination: 25,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      companies: [],
      permissionName: "company",
      userIsCreatingOrEditingData: false,
    };
  },

  mounted() {
    this.getInitialCompanies();
    bus.$on("reload-companies", () => this.getInitialCompanies())
    bus.$on("toggle-create-edit-flag", () => this.userIsCreatingOrEditingData = !this.userIsCreatingOrEditingData)
  },

  destroyed() {
    bus.$off("reload-companies");
    bus.$off("toggle-create-edit-flag");
  },

  computed: {
    headers() {
      return [
        { text: this.$i18n.t("Name"), varName: "name" },
        { text: this.$i18n.t("Email"), varName: "email" },
        { text: this.$i18n.t("Status"), varName: "status" },
      ]
    },
  },

  methods: {
    ...mapActions(["fetchUserCompanies", "deleteCompanyByID", "fetchAllCompanies"]),

    closeDialog() {
      this.dialog = false;
    },

    async getInitialCompanies() {
      this.loading = true;
      this.currentPageBot = 1
      this.currentPageTop = 1
      let queryParams = {
        "page[number]": 1,
        paginate: 25,
        "filter[company_level_id]": this.activeTab+1,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getCompanies(queryParams);
    },

    getCompaniesBottom() {
      this.loading = true;
      this.pagination = 25;
      if (this.companies.length >= 75) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      let queryParams = {
        "page[number]": this.currentPageBot,
        paginate: this.pagination,
        "filter[company_level_id]": this.activeTab+1,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getCompanies(queryParams, 'bottom');
    },

    async getCompaniesTop() {
      this.loading = true;
      this.pagination = 25;
      this.currentPageTop--;

      let queryParams = {
        "page[number]": this.currentPageTop,
        paginate: this.pagination,
        "filter[company_level_id]": this.activeTab+1,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getCompanies(queryParams, 'top');
    },

    getCompanies(queryParams, scrollDirection = null) {
      // if (
      //   this.isSuperAdmin ||
      //   (this.isAdmin && this.userHasManagementPermission("company_list"))
      // ) {
        this.getAllCompanies(queryParams, scrollDirection);
      // } else if (this.userHasManagementPermission("user_company_list")) {
      //   queryParams.id = this.$store.getters.currentUserID
        // companiesObject = this.getUserCompanies(queryParams, scrollDirection);
      // } else {
      //   this.loading = false;
      //   console.log("The user doesn't have the necessary permissions");
      // }
    },

    async getAllCompanies(queryParams = null, scrollDirection = null) {
      this.loading = true;
      try {
        let companies = await this.fetchAllCompanies(queryParams);
        this.processCompaniesData(companies, scrollDirection);

      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    /**
     * Get the list of user companies
     */
    async getUserCompanies(queryParams, scrollDirection = null) {
      try {
        this.loading = true;
        let companies = await this.fetchUserCompanies(queryParams);
        this.processCompaniesData(companies, scrollDirection);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    processCompaniesData(companiesData, scrollDirection = null) {
      let allCompanies = null;
      let currentScroll = null;
      let rowElement = null;
      switch (scrollDirection) {
        case 'top':
          this.lastPage = companiesData.meta.last_page;
          allCompanies = [...companiesData.data, ...this.companies];
          this.companies = Array.from(new Set(allCompanies.map(a => a.id)))
            .map(id => { return allCompanies.find(a => a.id === id) })
          if (this.companies.length > 75) {
            this.companies.splice(-(this.companies.length - 75));
            this.currentPageBot--;
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 25; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll + offset,
                behavior: "smooth",
              });
            }
          }
          break;
        case 'bottom':
          this.lastPage = companiesData.meta.last_page;
          allCompanies = [...this.companies, ...companiesData.data];
          this.companies = Array.from(new Set(allCompanies.map(a => a.id)))
            .map(id => { return allCompanies.find(a => a.id === id) })
          if (this.companies.length > 75) {
            this.companies = this.companies.slice(this.companies.length - 75);
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 2; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll - offset,
                behavior: "smooth",
              });
            }
          }
          break;
        default:
          this.companies = companiesData.data;
          this.lastPage = companiesData.meta.last_page;
          break;
      }
    },

    handleSearch: _.debounce(function (event) {
      if (event === "") {
        this.search = null;
      } else {
        this.search = event;
      }
      this.companies = [];
      this.getInitialCompanies();
    }, 1000),

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getCompaniesBottom();
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getCompaniesTop();
      }
    },

    onSortClick(sortOrder, index) {
      this.companies = []
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.getInitialCompanies();
    },

    handleAdd() {
      this.$emit('open-create-company-form')
    },

    handleTabChange(index) {
      this.activeTab = index;
      this.getInitialCompanies();
    },

    openDeleteDialog(){
      if (this.selectedObjectID) {
        this.deleteDialogIsOpen = true;
      }
    },

    async handleDelete() {
      try {
        this.loading = true;
        await this.deleteCompanyByID(this.selectedObjectID);
        this.getInitialCompanies();
        bus.$emit("clear-SelectedItems")
        bus.$emit("clear-selected-row")
        this.$emit('clear-selected-item')
      } catch (error) {
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.deleteDialogIsOpen = false
        this.loading = false;
      }
    }
  },
};
</script>

<style scoped>
.searchContainer {
  display: flex;
  align-items: center;
}

.tableDivContainer {
  height: 87%;
  margin-top: 5px;
}

.tabsContainer {
  height: 8%;
  display: flex;
  margin-top: 5px;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
}

.rightSideTitleActions {
  height: 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.buttons-container-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 20px;
}

.cancel-button-custom {
  background: transparent;
  border-radius: 0.25rem;
  border: 0.609px solid #29bdf0;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  /* 200% */
  text-transform: uppercase;
}

.rightSideTitleActions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.layer-search {
  flex: 0 0 auto;
  font-size: 12px;
  margin-right: 5px;
}

.layers-search .v-input__slot {
  min-height: 0 !important;
}

.layers-search .v-label {
  top: 4px !important;
  font: normal normal normal 12px/16px Segoe UI;
  color: #b1b6b9;
}

.span-custom {
  display: flex;
  padding: 0px 20px;
  color: #767676;
  font-family: Lato;
  font-size: 0.625rem;
  font-style: normal;
  font-weight: 400;
  line-height: 0.9375rem;
}

.tabs-custom {
  padding: 0px 20px;
}

.table-custom {
  height: 90%;
}

.table-buttons-custom {
  height: 70%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemManagement/Companies/TableView.vue